import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import OlMap from 'ol/Map';
import View from 'ol/View';
import Draw, { createBox } from 'ol/interaction/Draw';
import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import type Geometry from 'ol/geom/Geometry';
import type { Type as GeometryType } from 'ol/geom/Geometry';
import { Circle as CircleGeom, LineString, MultiLineString, MultiPoint, Point } from 'ol/geom';
import { fromCircle } from 'ol/geom/Polygon';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import type VectorImageLayer from 'ol/layer/VectorImage';
import { fromLonLat, transform, transformExtent } from 'ol/proj';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import { CANVAS_ICONS_BASE_URL } from '../../../../canvas/canvas-icons.constants';
import { CANVAS_BOUNDARY_GEOJSON_URL } from '../../../../canvas-tokens';
import {
  MAHARASHTRA_MAP_EXTENT,
  createMaharashtraBoundaryLayer,
  createMaharashtraOutsideDimLayer,
} from '../../../../canvas/maps/canvas-map-basemap.utils';
import {
  CatalogueApiService,
  type OgcConfiguredS3Bucket,
  OgcApiService,
  OgcAssetUploadConflictError,
  type StacAssetPayload,
  type StacCollectionPayload,
  type StacCollectionResponse,
  type StacItemPayload,
} from '@angular-workspace/services';
import { CanvasToolbarIconComponent } from '../../../../canvas/icons/canvas-toolbar-icon.component';
import {
  type CollectionBaseline,
  combineDateAndTimeUtc,
  compareStacDateTimes,
  formatStacDisplayDateTime,
  hasCollectionBaselineChanges,
  hasItemBaselineChanges,
  isDateTimeWithinRange,
  type ItemBaseline,
  normalizeStacUtcDateTime,
  parseStacDateTime,
  serializeItemProperties,
  stacTimeInputValue,
} from '../stac-datetime.util';
import {
  formatAssetByteSize,
  parseStacItemAssets,
  sanitizeDownloadFilename,
  type StacExistingAssetRow,
} from '../stac-assets.util';
import { syncRasterDataUploadStatus } from '../raster-data-upload-status.util';

export type StacFlowScope =
  | 'full'
  | 'embedded'
  | 'assets-only'
  | 'collection-edit'
  | 'item-edit';
type StacOnboardingStep = 'collection' | 'item' | 'assets';
type BboxTuple = [number, number, number, number];
type StacItemMode = 'new' | 'existing';
type MapDrawMode = 'collection-bbox' | 'item-geometry';
type MapDrawTool = 'marker' | 'line' | 'polygon' | 'rectangle' | 'circle';
type CalendarField = 'dateRange' | 'startDate' | 'endDate' | 'itemDate';
type TimeField = 'startDate' | 'endDate' | 'itemDate';
type AssetsItemSource = 'new' | 'existing';
type AssetsTab = 'view' | 'upload';
type GeoJsonGeometry = { type: string; coordinates: unknown };

interface TimePopoverState {
  field: TimeField;
  top: number;
  left: number;
  width: number;
}

interface CalendarPopoverState {
  field: CalendarField;
  month: Date;
  top: number;
  left: number;
  width: number;
  /** ISO date anchor while picking the second end of a collection date range. */
  rangeAnchor: string | null;
}

interface CalendarDay {
  date: Date;
  inCurrentMonth: boolean;
}

interface StacGeometrySelection {
  bbox: BboxTuple;
  geometry: GeoJsonGeometry;
  geometryType: string;
}

interface AssetUploadState {
  fileName: string;
  progress: number;
  status: string;
}

interface ItemPropertyRow {
  id: string;
  key: string;
  value: string;
}

interface AssetActionDropdownState {
  asset: StacExistingAssetRow;
  top: number;
  left: number;
  openAbove: boolean;
}

const CRS84 = 'http://www.opengis.net/def/crs/OGC/1.3/CRS84';

function trimmedRequired(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    return value ? null : { required: true };
  };
}

function dateRangeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const start = String(control.get('startDate')?.value ?? '').trim();
    const end = String(control.get('endDate')?.value ?? '').trim();
    if (!start || !end) return null;
    return compareStacDateTimes(end, start) >= 0 ? null : { dateRange: true };
  };
}

function stacItemIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) return { required: true };
    if (value.length > 50) return { maxlength: true };
    return /^[a-zA-Z0-9_-]+$/.test(value) ? null : { stacItemId: true };
  };
}

@Component({
  selector: 'lib-stac-collection-onboarding',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CanvasToolbarIconComponent],
  templateUrl: './stac-collection-onboarding.component.html',
  styleUrl: './stac-collection-onboarding.component.scss',
})
export class StacCollectionOnboardingComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly ogcApi = inject(OgcApiService);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly iconsBaseUrl = inject(CANVAS_ICONS_BASE_URL);
  private readonly boundaryGeoJsonUrl = inject(CANVAS_BOUNDARY_GEOJSON_URL, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly bboxMapEl =
    viewChild<ElementRef<HTMLDivElement>>('bboxMap');
  private readonly bboxPreviewMapEl =
    viewChild<ElementRef<HTMLDivElement>>('bboxPreviewMap');
  private readonly geoJsonFileInput =
    viewChild<ElementRef<HTMLInputElement>>('geoJsonFileInput');

  readonly itemId = input<string | null>(null);
  readonly datasetTitle = input('');
  readonly datasetDescription = input('');
  readonly showModalPreviousButton = input(true);
  readonly flowScope = input<StacFlowScope>('full');
  readonly embeddedStep = input<'collection' | 'item' | null>(null);
  readonly initialCollectionId = input<string | null>(null);
  readonly initialItemId = input<string | null>(null);
  readonly checkExistingCollection = input(false);

  readonly modalDismiss = output<void>();
  readonly modalPrevious = output<void>();
  readonly collectionCreated = output<{ id: string }>();
  readonly collectionStepComplete = output<{ id: string }>();
  readonly itemStepComplete = output<{ collectionId: string; itemId: string }>();
  readonly existingCollectionDetected = output<boolean>();
  readonly onboardingError = output<string>();
  readonly onboardingWarning = output<string>();
  readonly onboardingSuccess = output<string>();

  readonly activeStep = signal<StacOnboardingStep>('collection');
  readonly itemMode = signal<StacItemMode>('new');
  readonly collectionCheckLoading = signal(false);
  readonly resumedFromExistingApi = signal(false);
  readonly collectionBaseline = signal<CollectionBaseline | null>(null);
  readonly itemBaseline = signal<ItemBaseline | null>(null);
  readonly bbox = signal<BboxTuple | null>(null);
  readonly pendingBbox = signal<BboxTuple | null>(null);
  readonly collectionMapGeometry = signal<StacGeometrySelection | null>(null);
  readonly pendingCollectionGeometry = signal<StacGeometrySelection | null>(null);
  readonly itemGeometry = signal<StacGeometrySelection | null>(null);
  readonly pendingItemGeometry = signal<StacGeometrySelection | null>(null);
  readonly mapDrawMode = signal<MapDrawMode>('collection-bbox');
  readonly activeDrawTool = signal<MapDrawTool>('rectangle');
  readonly pendingGeometryType = signal('');
  readonly geoJsonHint = signal<string | null>(null);
  readonly geoJsonPasteText = signal('');
  readonly showGeoJsonPaste = signal(false);
  readonly shapeMenuOpen = signal(false);
  readonly bboxMapOpen = signal(false);
  readonly submitting = signal(false);
  readonly itemSubmitting = signal(false);
  readonly assetsSubmitting = signal(false);
  private readonly formRevision = signal(0);
  readonly createdCollectionId = signal<string | null>(null);
  readonly createdItemId = signal<string | null>(null);
  readonly existingItems = signal<StacItemPayload[]>([]);
  readonly existingItemsLoading = signal(false);
  readonly existingItemDetailsLoading = signal(false);
  readonly selectedExistingItemId = signal('');
  readonly buckets = signal<OgcConfiguredS3Bucket[]>([]);
  readonly bucketsLoading = signal(false);
  readonly selectedBucketId = signal('');
  readonly storageDropdownOpen = signal(false);
  readonly existingItemDropdownOpen = signal(false);
  readonly assetRoleDropdownIndex = signal<number | null>(null);
  readonly uploadStates = signal<AssetUploadState[]>([]);
  readonly calendarPopover = signal<CalendarPopoverState | null>(null);
  readonly timePopover = signal<TimePopoverState | null>(null);
  readonly assetsItemSource = signal<AssetsItemSource>('new');
  readonly assetsTab = signal<AssetsTab>('upload');
  readonly existingItemAssets = signal<StacExistingAssetRow[]>([]);
  readonly existingItemAssetsLoading = signal(false);
  readonly downloadingAssetId = signal<string | null>(null);
  readonly assetActionDropdown = signal<AssetActionDropdownState | null>(null);
  readonly itemProperties = signal<ItemPropertyRow[]>([]);
  readonly propertiesModalOpen = signal(false);
  readonly propertiesDraft = signal<ItemPropertyRow[]>([]);
  readonly propertiesModalError = signal<string | null>(null);
  readonly expandedAssetIndex = signal<number | null>(0);
  readonly assetFileDragActive = signal<number | null>(null);

  readonly collectionForm = this.fb.group(
    {
      title: ['', [trimmedRequired(), Validators.maxLength(180)]],
      description: ['', [trimmedRequired(), Validators.maxLength(2000)]],
      startDate: ['', [Validators.required]],
      endDate: ['', [Validators.required]],
    },
    { validators: [dateRangeValidator()] },
  );

  readonly itemForm = this.fb.group({
    id: ['', [stacItemIdValidator()]],
    datetime: ['', [Validators.required]],
  });

  readonly assetsForm = this.fb.group({
    rows: this.fb.array([this.createAssetRow()]),
  });

  readonly assetRoles = ['thumbnail', 'overview', 'metadata', 'data'] as const;
  readonly descriptionMaxLength = 2000;

  readonly descriptionLength = computed(() => {
    this.formRevision();
    return String(this.collectionForm.controls.description.value ?? '').length;
  });

  readonly steps: readonly { id: StacOnboardingStep; label: string }[] = [
    { id: 'collection', label: 'Collection' },
    { id: 'item', label: 'Item' },
    { id: 'assets', label: 'Assets' },
  ];

  readonly itemEditSubview = signal<'item' | 'assets'>('item');

  readonly displayedStep = computed<StacOnboardingStep>(() => {
    if (this.flowScope() === 'embedded') {
      return this.embeddedStep() ?? 'collection';
    }
    if (this.flowScope() === 'collection-edit') {
      return 'collection';
    }
    if (this.flowScope() === 'item-edit') {
      return this.itemEditSubview();
    }
    return this.activeStep();
  });

  readonly activeStepIndex = computed(() =>
    this.steps.findIndex((step) => step.id === this.activeStep()),
  );

  readonly isEditFlow = computed(() => this.resumedFromExistingApi());

  readonly hasCollectionChanges = computed(() => {
    this.formRevision();
    return hasCollectionBaselineChanges(this.currentCollectionBaseline(), this.collectionBaseline());
  });

  readonly hasItemChanges = computed(() => {
    this.formRevision();
    return hasItemBaselineChanges(this.currentItemBaseline(), this.itemBaseline());
  });

  readonly showAssetsTabs = computed(
    () => this.assetsItemSource() === 'existing' && this.existingItemAssets().length > 0,
  );

  readonly canSubmitCollection = computed(() => {
    this.formRevision();
    return (
      !this.submitting() &&
      this.collectionForm.valid &&
      !!this.bbox() &&
      !!this.itemId()?.trim()
    );
  });

  readonly showExistingItemTab = computed(
    () => !this.existingItemsLoading() && this.existingItems().length > 0,
  );

  readonly canSubmitItem = computed(() => {
    this.formRevision();
    if (this.itemSubmitting()) return false;
    if (this.flowScope() === 'item-edit') {
      if (this.itemMode() === 'new') {
        return (
          this.itemForm.valid && !!this.itemGeometry() && !this.itemDateOutsideCollectionRange()
        );
      }
      return (
        !!this.selectedExistingItemId().trim() &&
        this.itemForm.valid &&
        !!this.itemGeometry() &&
        !this.itemDateOutsideCollectionRange()
      );
    }
    if (this.isEditFlow() && this.itemMode() === 'existing' && this.showExistingItemTab()) {
      return (
        !!this.selectedExistingItemId().trim() &&
        this.itemForm.valid &&
        !!this.itemGeometry() &&
        !this.itemDateOutsideCollectionRange()
      );
    }
    if (this.itemMode() === 'existing' && this.showExistingItemTab() && !this.isEditFlow()) {
      return !!this.selectedExistingItemId().trim();
    }
    return this.itemForm.valid && !!this.itemGeometry() && !this.itemDateOutsideCollectionRange();
  });

  readonly canSubmitAssets = computed(() => {
    this.formRevision();
    return (
      !this.assetsSubmitting() &&
      !!this.createdItemId() &&
      !!this.selectedBucketId() &&
      this.assetRows.length > 0 &&
      this.assetsForm.valid
    );
  });

  private map: OlMap | null = null;
  private draw: Draw | null = null;
  private vectorSource: VectorSource | null = null;
  private bboxOverlaySource: VectorSource | null = null;
  private previewMap: OlMap | null = null;
  private previewGeometrySource: VectorSource | null = null;
  private previewBboxOverlaySource: VectorSource | null = null;
  private placementMarkerStyle: Style | null = null;
  private pendingMarkerCoords: [number, number][] = [];
  private maharashtraBoundaryLayer: VectorLayer<VectorSource> | null = null;
  private maharashtraDimLayer: VectorImageLayer<VectorSource> | null = null;
  private previewMaharashtraBoundaryLayer: VectorLayer<VectorSource> | null = null;
  private previewMaharashtraDimLayer: VectorImageLayer<VectorSource> | null = null;
  private readonly mapProjection = 'EPSG:3857';
  private assetsOnlyInitialized = false;
  private existingCollectionCheckInitialized = false;
  private collectionEditInitDone = false;
  private itemEditInitDone = false;

  constructor() {
    this.collectionForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));
    this.collectionForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));
    this.itemForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));
    this.itemForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));
    this.assetsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));
    this.assetsForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formRevision.update((n) => n + 1));

    effect(() => {
      const step = this.displayedStep();
      const collectionBox = this.bbox();
      const itemGeom = this.itemGeometry();
      const collectionLoading = this.collectionCheckLoading();
      const itemDetailsLoading = this.existingItemDetailsLoading();
      untracked(() => {
        if (collectionLoading || itemDetailsLoading) {
          return;
        }
        if (step === 'collection' && collectionBox) {
          this.scheduleGeometryPreviewRefresh('collection');
        } else if (step === 'item' && itemGeom) {
          this.scheduleGeometryPreviewRefresh('item');
        } else if (step !== 'assets') {
          this.destroyBboxPreviewMap();
        }
      });
    });

    effect(() => {
      if (this.flowScope() !== 'assets-only' || this.assetsOnlyInitialized) {
        return;
      }
      const collectionId = this.initialCollectionId()?.trim();
      const itemId = this.initialItemId()?.trim();
      untracked(() => {
        this.assetsOnlyInitialized = true;
        if (collectionId) {
          this.createdCollectionId.set(collectionId);
        }
        if (itemId) {
          this.createdItemId.set(itemId);
        }
        this.activeStep.set('assets');
        void this.prepareAssetsStep('existing');
      });
    });

    effect(() => {
      if (this.existingCollectionCheckInitialized || !this.checkExistingCollection()) {
        return;
      }
      const itemId = this.itemId()?.trim();
      if (
        !itemId ||
        this.flowScope() === 'embedded' ||
        this.flowScope() === 'assets-only' ||
        this.flowScope() === 'collection-edit' ||
        this.flowScope() === 'item-edit'
      ) {
        return;
      }
      untracked(() => {
        this.existingCollectionCheckInitialized = true;
        void this.tryResumeExistingCollection(itemId);
      });
    });

    effect(() => {
      if (this.collectionEditInitDone || this.flowScope() !== 'collection-edit') {
        return;
      }
      const itemId = this.itemId()?.trim();
      if (!itemId) {
        return;
      }
      untracked(() => {
        this.collectionEditInitDone = true;
        void this.loadCollectionForEdit(itemId);
      });
    });

    effect(() => {
      if (this.itemEditInitDone || this.flowScope() !== 'item-edit') {
        return;
      }
      const itemId = this.itemId()?.trim();
      if (!itemId) {
        return;
      }
      untracked(() => {
        this.itemEditInitDone = true;
        void this.initializeItemEdit(itemId);
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyMap();
    this.destroyBboxPreviewMap();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.storageDropdownOpen.set(false);
    this.existingItemDropdownOpen.set(false);
    this.assetRoleDropdownIndex.set(null);
    this.closeTimePopover();
    this.closeAssetActionDropdown();
    if (this.propertiesModalOpen()) {
      this.closePropertiesModal();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (!target.closest('.stac-map-modal__shape-split')) {
      this.shapeMenuOpen.set(false);
    }
    if (!target.closest('.stac-custom-dropdown')) {
      this.storageDropdownOpen.set(false);
      this.existingItemDropdownOpen.set(false);
      this.assetRoleDropdownIndex.set(null);
    }
    if (
      !target.closest('.stac-existing-assets__action-cell') &&
      !target.closest('.stac-asset-action-overlay')
    ) {
      this.closeAssetActionDropdown();
    }
    if (target.closest('.stac-calendar-popover') || target.closest('.stac-time-popover')) {
      return;
    }
    this.closeCalendar();
    this.closeTimePopover();
  }

  openCalendar(field: CalendarField, event: MouseEvent): void {
    this.closeTimePopover();
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(340, Math.max(320, rect.width));
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const estimatedHeight = 348;
    const top =
      viewportHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight
        ? rect.top - estimatedHeight - 8
        : rect.bottom + 8;
    const left = Math.min(Math.max(8, rect.left), viewportWidth - width - 8);
    const selected =
      field === 'dateRange'
        ? this.getCalendarFieldDate('startDate') ?? this.getCalendarFieldDate('endDate')
        : this.getCalendarFieldDate(field);
    const month = selected ?? new Date();
    month.setDate(1);
    const rangeAnchor =
      field === 'dateRange'
        ? String(this.collectionForm.controls.startDate.value ?? '').trim() || null
        : null;
    this.calendarPopover.set({
      field,
      month,
      top: Math.max(8, top),
      left,
      width,
      rangeAnchor,
    });
  }

  openDateRangeCalendar(event: MouseEvent): void {
    this.openCalendar('dateRange', event);
  }

  closeCalendar(): void {
    this.calendarPopover.set(null);
  }

  openTimePopover(field: TimeField, event: MouseEvent): void {
    event.stopPropagation();
    this.closeCalendar();
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const width = 220;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const estimatedHeight = 280;
    const top =
      viewportHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight
        ? rect.top - estimatedHeight - 8
        : rect.bottom + 8;
    const left = Math.min(Math.max(8, rect.left), viewportWidth - width - 8);
    this.timePopover.set({
      field,
      top: Math.max(8, top),
      left,
      width,
    });
  }

  closeTimePopover(): void {
    this.timePopover.set(null);
  }

  timeHours(): number[] {
    return Array.from({ length: 24 }, (_, index) => index);
  }

  timeMinutes(): number[] {
    return Array.from({ length: 60 }, (_, index) => index);
  }

  formatTimePart(value: number): string {
    return String(value).padStart(2, '0');
  }

  isTimeHourSelected(hour: number): boolean {
    const popover = this.timePopover();
    if (!popover) return false;
    const [currentHour] = this.timeInputValue(popover.field).split(':');
    return Number(currentHour) === hour;
  }

  isTimeMinuteSelected(minute: number): boolean {
    const popover = this.timePopover();
    if (!popover) return false;
    const parts = this.timeInputValue(popover.field).split(':');
    return Number(parts[1]) === minute;
  }

  selectTimeHour(hour: number): void {
    const popover = this.timePopover();
    if (!popover) return;
    const parts = this.timeInputValue(popover.field).split(':');
    const minute = parts[1] ?? '00';
    this.applyTimeSelection(popover.field, `${this.formatTimePart(hour)}:${minute}`);
  }

  selectTimeMinute(minute: number): void {
    const popover = this.timePopover();
    if (!popover) return;
    const parts = this.timeInputValue(popover.field).split(':');
    const hour = parts[0] ?? '00';
    this.applyTimeSelection(popover.field, `${hour}:${this.formatTimePart(minute)}`);
    this.closeTimePopover();
  }

  previousCalendarMonth(): void {
    this.calendarPopover.update((state) => {
      if (!state) return state;
      const month = new Date(state.month);
      month.setMonth(month.getMonth() - 1);
      return { ...state, month };
    });
  }

  nextCalendarMonth(): void {
    this.calendarPopover.update((state) => {
      if (!state) return state;
      const month = new Date(state.month);
      month.setMonth(month.getMonth() + 1);
      return { ...state, month };
    });
  }

  calendarMonthLabel(month: Date): string {
    return month.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  calendarWeekDays(): string[] {
    return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  }

  calendarDays(month: Date): CalendarDay[] {
    const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(firstOfMonth);
    const mondayIndex = (firstOfMonth.getDay() + 6) % 7;
    start.setDate(firstOfMonth.getDate() - mondayIndex);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return {
        date,
        inCurrentMonth: date.getMonth() === month.getMonth(),
      };
    });
  }

  selectCalendarDate(field: CalendarField, date: Date): void {
    if (field === 'dateRange') {
      this.selectDateRangeDate(date);
      return;
    }
    if (this.isCalendarDayOutsideCollectionRange(date, field)) return;
    const isoDate = this.toIsoDate(date);
    if (field === 'itemDate') {
      const current = String(this.itemForm.controls.datetime.value ?? '').trim();
      const time = stacTimeInputValue(current);
      this.itemForm.controls.datetime.setValue(combineDateAndTimeUtc(isoDate, time));
      this.itemForm.controls.datetime.markAsTouched();
    } else if (field === 'startDate' || field === 'endDate') {
      const current = String(this.collectionForm.controls[field].value ?? '').trim();
      const time = stacTimeInputValue(current);
      const nextValue = combineDateAndTimeUtc(isoDate, time);
      this.collectionForm.controls[field].setValue(nextValue);
      this.collectionForm.controls[field].markAsTouched();
      if (field === 'startDate') {
        const endValue = String(this.collectionForm.controls.endDate.value ?? '').trim();
        if (endValue && compareStacDateTimes(endValue, nextValue) < 0) {
          this.collectionForm.controls.endDate.setValue('');
          this.collectionForm.controls.endDate.markAsUntouched();
        }
      }
      this.collectionForm.updateValueAndValidity();
    }
    this.formRevision.update((n) => n + 1);
    this.closeCalendar();
  }

  private selectDateRangeDate(date: Date): void {
    const isoDate = this.toIsoDate(date);
    const popover = this.calendarPopover();
    const anchor = popover?.rangeAnchor;

    if (!anchor) {
      const currentStart = String(this.collectionForm.controls.startDate.value ?? '').trim();
      const startTime = stacTimeInputValue(currentStart);
      this.collectionForm.controls.startDate.setValue(combineDateAndTimeUtc(isoDate, startTime));
      this.collectionForm.controls.endDate.setValue('');
      this.collectionForm.controls.startDate.markAsTouched();
      this.calendarPopover.update((state) =>
        state ? { ...state, rangeAnchor: isoDate } : state,
      );
      this.formRevision.update((n) => n + 1);
      return;
    }

    const currentStart = String(this.collectionForm.controls.startDate.value ?? '').trim();
    const currentEnd = String(this.collectionForm.controls.endDate.value ?? '').trim();
    const startTime = stacTimeInputValue(currentStart);
    const endTime = stacTimeInputValue(currentEnd);
    let startIso = combineDateAndTimeUtc(anchor, startTime);
    let endIso = combineDateAndTimeUtc(isoDate, endTime);
    if (compareStacDateTimes(endIso, startIso) < 0) {
      [startIso, endIso] = [endIso, startIso];
    }
    this.collectionForm.controls.startDate.setValue(startIso);
    this.collectionForm.controls.endDate.setValue(endIso);
    this.collectionForm.controls.endDate.markAsTouched();
    this.formRevision.update((n) => n + 1);
    this.closeCalendar();
  }

  dateRangeDisplayValue(): string {
    const start = this.getCalendarFieldDate('startDate');
    const end = this.getCalendarFieldDate('endDate');
    if (start && end) {
      return `${formatStacDisplayDateTime(start)} – ${formatStacDisplayDateTime(end)}`;
    }
    if (start) {
      return `${formatStacDisplayDateTime(start)} – Choose end date and time`;
    }
    return '';
  }

  dateRangeCalendarHint(): string {
    const popover = this.calendarPopover();
    if (!popover || popover.field !== 'dateRange') return '';
    return popover.rangeAnchor ? 'Choose end date' : 'Choose start date';
  }

  calendarDisplayValue(field: CalendarField): string {
    const date = this.getCalendarFieldDate(field);
    return date ? formatStacDisplayDateTime(date) : '';
  }

  timeInputValue(field: 'startDate' | 'endDate' | 'itemDate'): string {
    if (field === 'itemDate') {
      return stacTimeInputValue(String(this.itemForm.controls.datetime.value ?? ''));
    }
    return stacTimeInputValue(String(this.collectionForm.controls[field].value ?? ''));
  }

  applyTimeSelection(field: TimeField, time: string): void {
    if (field === 'itemDate') {
      const current = String(this.itemForm.controls.datetime.value ?? '').trim();
      const datePart = this.extractDatePart(current) ?? this.toIsoDate(new Date());
      this.itemForm.controls.datetime.setValue(combineDateAndTimeUtc(datePart, time));
      this.itemForm.controls.datetime.markAsTouched();
    } else {
      const current = String(this.collectionForm.controls[field].value ?? '').trim();
      const datePart = this.extractDatePart(current) ?? this.toIsoDate(new Date());
      const nextValue = combineDateAndTimeUtc(datePart, time);
      this.collectionForm.controls[field].setValue(nextValue);
      this.collectionForm.controls[field].markAsTouched();
      if (field === 'startDate') {
        const endValue = String(this.collectionForm.controls.endDate.value ?? '').trim();
        if (endValue && compareStacDateTimes(endValue, nextValue) < 0) {
          this.collectionForm.controls.endDate.setValue('');
          this.collectionForm.controls.endDate.markAsUntouched();
        }
      }
      this.collectionForm.updateValueAndValidity();
    }
    this.formRevision.update((n) => n + 1);
  }

  isCalendarDaySelected(date: Date, field: CalendarField): boolean {
    const iso = this.toIsoDate(date);
    if (field === 'dateRange') {
      const popover = this.calendarPopover();
      const anchor = popover?.rangeAnchor;
      const start = this.extractDatePart(String(this.collectionForm.controls.startDate.value ?? ''));
      const end = this.extractDatePart(String(this.collectionForm.controls.endDate.value ?? ''));
      if (anchor && iso === anchor) return true;
      return iso === start || iso === end;
    }
    if (field === 'itemDate') {
      return this.extractDatePart(String(this.itemForm.controls.datetime.value ?? '')) === iso;
    }
    if (field === 'startDate' || field === 'endDate') {
      return this.extractDatePart(String(this.collectionForm.controls[field].value ?? '')) === iso;
    }
    return false;
  }

  isCalendarDayInRange(date: Date, field: CalendarField): boolean {
    if (field !== 'dateRange') return false;
    const start = this.getCalendarFieldDate('startDate');
    if (!start) return false;
    const popover = this.calendarPopover();
    const anchorIso = popover?.rangeAnchor;
    const end =
      this.getCalendarFieldDate('endDate') ??
      (anchorIso ? this.parseIsoDate(anchorIso) : null);
    if (!end) return false;
    const time = date.getTime();
    const min = Math.min(start.getTime(), end.getTime());
    const max = Math.max(start.getTime(), end.getTime());
    return time > min && time < max;
  }

  isCalendarDayToday(date: Date): boolean {
    return this.toIsoDate(date) === this.toIsoDate(new Date());
  }

  /** Returns true if the collection has a date range constraint (at least one bound). */
  collectionHasDateRange(): boolean {
    const start = String(this.collectionForm.controls.startDate.value ?? '').trim();
    const end = String(this.collectionForm.controls.endDate.value ?? '').trim();
    return !!(start || end);
  }

  /** Human-readable label for the collection date range constraint. */
  collectionDateRangeDisplay(): string {
    const start = String(this.collectionForm.controls.startDate.value ?? '').trim();
    const end = String(this.collectionForm.controls.endDate.value ?? '').trim();
    const startDate = start ? parseStacDateTime(start) : null;
    const endDate = end ? parseStacDateTime(end) : null;
    if (startDate && endDate) {
      return `${formatStacDisplayDateTime(startDate)} – ${formatStacDisplayDateTime(endDate)}`;
    }
    if (startDate) return `from ${formatStacDisplayDateTime(startDate)}`;
    if (endDate) return `until ${formatStacDisplayDateTime(endDate)}`;
    return '';
  }

  /** True when the item date has been set but falls outside the collection date range. */
  itemDateOutsideCollectionRange(): boolean {
    const datetime = String(this.itemForm.controls.datetime.value ?? '').trim();
    if (!datetime) return false;
    const startIso = String(this.collectionForm.controls.startDate.value ?? '').trim();
    const endIso = String(this.collectionForm.controls.endDate.value ?? '').trim();
    if (!startIso && !endIso) return false;
    return !isDateTimeWithinRange(datetime, startIso, endIso);
  }

  /** True when the given calendar day is outside allowed bounds for the active field. */
  isCalendarDayOutsideCollectionRange(date: Date, field: CalendarField): boolean {
    const iso = this.toIsoDate(date);
    if (field === 'endDate') {
      const startIso = this.extractDatePart(String(this.collectionForm.controls.startDate.value ?? ''));
      return !!(startIso && iso < startIso);
    }
    if (field === 'startDate') {
      const endIso = this.extractDatePart(String(this.collectionForm.controls.endDate.value ?? ''));
      return !!(endIso && iso > endIso);
    }
    if (field !== 'itemDate') return false;
    const startIso = this.extractDatePart(String(this.collectionForm.controls.startDate.value ?? ''));
    const endIso = this.extractDatePart(String(this.collectionForm.controls.endDate.value ?? ''));
    if (!startIso && !endIso) return false;
    if (startIso && iso < startIso) return true;
    if (endIso && iso > endIso) return true;
    return false;
  }

  itemPropertiesCount(): number {
    return this.itemProperties().length;
  }

  openPropertiesModal(): void {
    this.closeCalendar();
    this.closeTimePopover();
    const current = this.itemProperties();
    this.propertiesDraft.set(
      current.length > 0
        ? current.map((row) => ({ ...row }))
        : [this.createPropertyRow()],
    );
    this.propertiesModalError.set(null);
    this.propertiesModalOpen.set(true);
  }

  closePropertiesModal(): void {
    this.propertiesModalOpen.set(false);
    this.propertiesModalError.set(null);
  }

  addPropertyDraftRow(): void {
    if (!this.canAddPropertyDraftRow()) return;
    this.propertiesDraft.update((rows) => [...rows, this.createPropertyRow()]);
  }

  canAddPropertyDraftRow(): boolean {
    const rows = this.propertiesDraft();
    if (rows.length === 0) return false;
    return rows.every(
      (row) => row.key.trim().length > 0 && row.value.trim().length > 0,
    );
  }

  removePropertyDraftRow(id: string): void {
    this.propertiesDraft.update((rows) => {
      const next = rows.filter((row) => row.id !== id);
      return next.length > 0 ? next : [this.createPropertyRow()];
    });
  }

  updatePropertyDraftKey(id: string, event: Event): void {
    const key = (event.target as HTMLInputElement).value;
    this.propertiesDraft.update((rows) =>
      rows.map((row) => (row.id === id ? { ...row, key } : row)),
    );
    this.propertiesModalError.set(null);
  }

  updatePropertyDraftValue(id: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.propertiesDraft.update((rows) =>
      rows.map((row) => (row.id === id ? { ...row, value } : row)),
    );
    this.propertiesModalError.set(null);
  }

  submitPropertiesModal(): void {
    const rows = this.propertiesDraft();
    const properties: ItemPropertyRow[] = [];
    const keys = new Set<string>();

    for (const row of rows) {
      const key = row.key.trim();
      const value = row.value.trim();
      if (!key && !value) continue;
      if (!key) {
        this.propertiesModalError.set('Each property row needs a key.');
        return;
      }
      if (key.toLowerCase() === 'datetime') {
        this.propertiesModalError.set(
          '"datetime" is set from the Date field and cannot be added here.',
        );
        return;
      }
      if (keys.has(key)) {
        this.propertiesModalError.set(`Duplicate key "${key}".`);
        return;
      }
      keys.add(key);
      properties.push({ ...row, key, value });
    }

    this.itemProperties.set(properties);
    this.closePropertiesModal();
    this.formRevision.update((n) => n + 1);
  }

  formatPropertyPreviewValue(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    return trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
  }

  get assetRows(): FormArray<FormGroup> {
    return this.assetsForm.controls.rows as FormArray<FormGroup>;
  }

  createAssetRow(): FormGroup {
    return this.fb.group({
      title: ['', [trimmedRequired()]],
      description: ['', [trimmedRequired()]],
      role: ['data', [Validators.required]],
      fileName: ['', [Validators.required]],
      fileObject: new FormControl<File | null>(null, [Validators.required]),
    });
  }

  onModalPrevious(): void {
    this.modalPrevious.emit();
  }

  onModalDismiss(): void {
    this.modalDismiss.emit();
  }

  stepState(step: StacOnboardingStep): 'active' | 'complete' | 'upcoming' {
    const current = this.activeStepIndex();
    const index = this.steps.findIndex((s) => s.id === step);
    if (index === current) return 'active';
    return index < current ? 'complete' : 'upcoming';
  }

  assetsItemLabel(): string {
    return (
      this.createdItemId()?.trim() ||
      this.selectedExistingItemId().trim() ||
      String(this.itemForm.controls.id.value ?? '').trim() ||
      'STAC item'
    );
  }

  openBboxMap(): void {
    this.closeCalendar();
    this.closeTimePopover();
    const saved = this.collectionMapGeometry();
    const bbox = saved?.bbox ?? this.bbox();
    this.pendingBbox.set(bbox);
    this.pendingItemGeometry.set(null);
    this.mapDrawMode.set('collection-bbox');

    this.activeDrawTool.set('rectangle');

    if (saved) {
      this.pendingCollectionGeometry.set(saved);
      this.pendingGeometryType.set(saved.geometryType);
    } else if (bbox) {
      const restored: StacGeometrySelection = {
        bbox,
        geometry: this.bboxToRectangleGeometry(bbox),
        geometryType: 'Rectangle',
      };
      this.pendingCollectionGeometry.set(restored);
      this.pendingGeometryType.set('Rectangle');
    } else {
      this.pendingCollectionGeometry.set(null);
      this.pendingGeometryType.set('');
    }

    this.showGeoJsonPaste.set(false);
    this.shapeMenuOpen.set(false);
    this.geoJsonHint.set(null);
    this.bboxMapOpen.set(true);
    window.setTimeout(() => this.initMap(), 0);
  }

  closeBboxMap(): void {
    this.bboxMapOpen.set(false);
    this.pendingBbox.set(null);
    this.pendingCollectionGeometry.set(null);
    this.pendingItemGeometry.set(null);
    this.pendingGeometryType.set('');
    this.showGeoJsonPaste.set(false);
    this.shapeMenuOpen.set(false);
    this.geoJsonHint.set(null);
    this.destroyMap();
  }

  clearPendingBbox(): void {
    this.clearPendingMapSelection();
  }

  private clearPendingMapSelection(): void {
    this.pendingBbox.set(null);
    this.pendingCollectionGeometry.set(null);
    this.pendingItemGeometry.set(null);
    this.pendingGeometryType.set('');
    this.clearMapDrawArtifacts();
  }

  applyPendingBbox(): void {
    if (this.mapDrawMode() === 'item-geometry') {
      const geometry = this.pendingItemGeometry();
      if (!geometry) {
        this.notifyError('Upload or paste geometry before saving it.');
        return;
      }
      this.itemGeometry.set(geometry);
      this.bboxMapOpen.set(false);
      this.destroyMap();
      this.formRevision.update((n) => n + 1);
      return;
    }

    const next = this.pendingBbox();
    if (!next) {
      this.notifyError( 'Draw a bounding box before applying it.');
      return;
    }
    this.bbox.set(next);
    this.collectionMapGeometry.set(this.pendingCollectionGeometry());
    this.bboxMapOpen.set(false);
    this.destroyMap();
    this.formRevision.update((n) => n + 1);
  }

  bboxValue(index: number): string {
    const box = this.bbox();
    if (!box) return '';
    return this.formatCoordinate(box[index] ?? 0);
  }

  itemBboxValue(index: number): string {
    const box = this.itemGeometry()?.bbox;
    if (!box) return '';
    return this.formatCoordinate(box[index] ?? 0);
  }

  clearCollectionBbox(): void {
    this.bbox.set(null);
    this.collectionMapGeometry.set(null);
    this.geoJsonPasteText.set('');
    this.destroyBboxPreviewMap();
    this.formRevision.update((n) => n + 1);
  }

  clearItemGeometry(): void {
    this.itemGeometry.set(null);
    this.destroyBboxPreviewMap();
    this.formRevision.update((n) => n + 1);
  }

  markerMapCursor(): string {
    if (this.activeDrawTool() !== 'marker') {
      return '';
    }
    const iconUrl = `${this.iconsBaseUrl.replace(/\/$/, '')}/add-location-map.svg`;
    return `url("${iconUrl}") 12 24, crosshair`;
  }

  zoomModalMap(delta: number): void {
    this.zoomMapBy(this.map, delta);
  }

  zoomPreviewMap(delta: number): void {
    this.zoomMapBy(this.previewMap, delta);
  }

  pendingBboxLabel(): string {
    if (this.mapDrawMode() === 'item-geometry') {
      const geometry = this.pendingItemGeometry();
      return geometry
        ? geometry.bbox.map((value) => this.formatCoordinate(value)).join(', ')
        : '';
    }
    const box = this.pendingBbox();
    return box ? box.map((value) => this.formatCoordinate(value)).join(', ') : '';
  }

  hasPendingMapSelection(): boolean {
    return this.mapDrawMode() === 'item-geometry'
      ? !!this.pendingItemGeometry()
      : !!this.pendingBbox();
  }

  mapModalTitle(): string {
    return this.mapDrawMode() === 'item-geometry'
      ? 'Add Geometry'
      : 'Select Bbox';
  }

  mapModalDescription(): string {
    return this.mapDrawMode() === 'item-geometry'
      ? 'Upload or paste one GeoJSON geometry for the STAC item. Its bbox is derived automatically.'
      : 'Draw or upload one geometry. The collection bbox is derived from its extent.';
  }

  readonly shapeDrawTools = [
    { id: 'circle' as const, label: 'Circle' },
    { id: 'rectangle' as const, label: 'Rectangle' },
    { id: 'polygon' as const, label: 'Polygon' },
  ];

  setDrawTool(tool: MapDrawTool): void {
    const previousTool = this.activeDrawTool();
    this.closeGeoJsonPaste();
    this.activeDrawTool.set(tool);
    this.shapeMenuOpen.set(false);
    this.geoJsonHint.set(null);
    if (tool !== previousTool) {
      this.clearMapDrawArtifacts();
      if (tool === 'marker') {
        this.pendingBbox.set(null);
        this.pendingCollectionGeometry.set(null);
        this.pendingItemGeometry.set(null);
        this.pendingGeometryType.set('');
      }
    }
    this.startDrawInteraction();
  }

  onShapeMainClick(): void {
    const tool = this.activeDrawTool();
    if (tool === 'circle' || tool === 'rectangle' || tool === 'polygon') {
      this.setDrawTool(tool);
    } else {
      this.setDrawTool('polygon');
    }
  }

  toggleShapeMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.shapeMenuOpen.update((open) => !open);
  }

  selectShapeTool(tool: MapDrawTool, event: MouseEvent): void {
    event.stopPropagation();
    this.setDrawTool(tool);
  }

  toggleGeoJsonPaste(): void {
    this.showGeoJsonPaste.update((open) => !open);
    this.shapeMenuOpen.set(false);
    if (this.showGeoJsonPaste()) {
      this.stopDrawInteraction();
    }
  }

  private closeGeoJsonPaste(): void {
    this.showGeoJsonPaste.set(false);
  }

  onGeoJsonPasteInput(event: Event): void {
    this.geoJsonPasteText.set((event.target as HTMLTextAreaElement).value);
  }

  onGeoJsonPaste(event: ClipboardEvent): void {
    window.setTimeout(() => {
      const target = event.target as HTMLTextAreaElement | null;
      if (!target) return;
      const formatted = this.formatGeoJsonText(target.value);
      this.geoJsonPasteText.set(formatted);
      target.value = formatted;
    }, 0);
  }

  applyPastedGeoJson(): void {
    const formatted = this.formatGeoJsonText(this.geoJsonPasteText());
    this.geoJsonPasteText.set(formatted);
    if (!formatted.trim()) {
      this.clearPendingMapSelection();
      this.geoJsonHint.set('GeoJSON cleared from the map.');
      return;
    }
    try {
      this.applyUploadedGeoJson(formatted);
      this.geoJsonHint.set('GeoJSON applied. BBOX derived from its extent.');
      this.showGeoJsonPaste.set(false);
    } catch (error) {
      this.geoJsonHint.set(
        error instanceof Error ? error.message : 'Could not parse that GeoJSON.',
      );
    }
  }

  drawInstruction(): string {
    if (this.mapDrawMode() === 'item-geometry') {
      return 'Upload a GeoJSON file or paste GeoJSON to define the item geometry.';
    }
    switch (this.activeDrawTool()) {
      case 'marker':
        return 'Click to place markers. Each click adds a marker; the bbox covers all markers.';
      case 'line':
        return 'Click to add line vertices. Double-click to finish.';
      case 'polygon':
        return 'Click to add polygon vertices. Double-click to finish.';
      case 'circle':
        return 'Drag from center to radius.';
      default:
        return 'Drag to draw a rectangle.';
    }
  }

  pendingGeometryLabel(): string {
    const type = this.pendingGeometryType();
    return type ? `${type} selected` : 'No geometry selected';
  }

  triggerGeoJsonUpload(): void {
    this.showGeoJsonPaste.set(true);
    this.shapeMenuOpen.set(false);
    this.stopDrawInteraction();
    this.geoJsonFileInput()?.nativeElement.click();
  }

  onGeoJsonFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension !== 'json' && extension !== 'geojson') {
      this.geoJsonHint.set('Upload a .json or .geojson file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        const formatted = this.formatGeoJsonText(text);
        this.geoJsonPasteText.set(formatted);
        this.showGeoJsonPaste.set(true);
        this.geoJsonHint.set('GeoJSON loaded. Review the code and click Apply GeoJSON.');
      } catch (error) {
        this.geoJsonHint.set(
          error instanceof Error ? error.message : 'Could not read that GeoJSON file.',
        );
      }
    };
    reader.onerror = () => this.geoJsonHint.set('Could not read that file.');
    reader.readAsText(file);
  }

  async submitCollection(): Promise<void> {
    if (!this.itemId()?.trim()) {
      this.notifyError('Dataset ID is missing for STAC onboarding.');
      return;
    }

    if (!this.collectionForm.valid || !this.bbox()) {
      this.collectionForm.markAllAsTouched();
      if (!this.bbox()) {
        this.notifyError('Draw a bounding box for the collection extent.');
      }
      return;
    }

    const existingCollectionId = this.createdCollectionId();
    const editFlowNoChanges = this.isEditFlow() && existingCollectionId && !this.hasCollectionChanges();

    if (editFlowNoChanges) {
      if (this.flowScope() === 'embedded') {
        this.collectionStepComplete.emit({ id: this.collectionId() });
      } else if (this.flowScope() !== 'collection-edit') {
        this.activeStep.set('item');
      }
      return;
    }

    const payload = this.buildCollectionPayload();
    try {
      this.submitting.set(true);
      if (existingCollectionId) {
        await this.ogcApi.updateStacCollection(existingCollectionId, payload);
        this.captureCollectionBaseline();
        this.notifySuccess('STAC collection updated successfully.');
        if (this.flowScope() === 'collection-edit') {
          return;
        }
      } else {
        await this.ogcApi.createStacCollection(payload);
        this.createdCollectionId.set(payload.id);
        this.collectionCreated.emit({ id: payload.id });
        this.notifySuccess('STAC collection created successfully.');
      }
      await this.loadExistingItems();
      if (this.flowScope() === 'embedded') {
        this.collectionStepComplete.emit({ id: this.collectionId() });
      } else {
        this.activeStep.set('item');
      }
    } catch (error) {
      const message = this.errorMessage(error);
      this.notifyError(message);
    } finally {
      this.submitting.set(false);
    }
  }

  continueToAssets(): void {
    void this.prepareAssetsStep();
    this.activeStep.set('assets');
  }

  backToItem(): void {
    this.activeStep.set('item');
  }

  openItemEditAssets(): void {
    const itemId = this.resolveExistingStacItemIdForSubmit() ?? '';
    if (!itemId) {
      this.notifyError('Select a STAC item before uploading assets.');
      return;
    }
    this.createdItemId.set(itemId);
    this.itemEditSubview.set('assets');
    void this.prepareAssetsStep('existing');
  }

  backFromItemEditAssets(): void {
    this.itemEditSubview.set('item');
  }

  backToCollection(): void {
    this.activeStep.set('collection');
  }

  private async loadCollectionForEdit(collectionId: string): Promise<void> {
    try {
      this.collectionCheckLoading.set(true);
      const collection = await this.ogcApi.getStacCollection(collectionId);
      if (!collection?.['id']) {
        this.notifyError('STAC collection not found for this dataset.');
        return;
      }
      this.applyCollectionFromApi(collection);
      this.createdCollectionId.set(String(collection['id']).trim());
      this.markResumedFromExistingApi();
      this.scheduleGeometryPreviewRefresh('collection');
    } catch (error) {
      this.notifyError(this.errorMessage(error));
    } finally {
      this.collectionCheckLoading.set(false);
    }
  }

  private async initializeItemEdit(collectionId: string): Promise<void> {
    try {
      this.collectionCheckLoading.set(true);
      const collection = await this.ogcApi.getStacCollection(collectionId);
      if (!collection?.['id']) {
        this.notifyError('STAC collection not found for this dataset.');
        return;
      }
      this.applyCollectionFromApi(collection);
      const id = String(collection['id']).trim();
      this.createdCollectionId.set(id);
      this.markResumedFromExistingApi();
      await this.loadExistingItems();
      this.itemMode.set(this.existingItems().length > 0 ? 'existing' : 'new');
      if (this.existingItems().length === 0) {
        this.notifyWarning('No STAC items found. Create a new item below.');
      }
    } catch (error) {
      this.notifyError(this.errorMessage(error));
    } finally {
      this.collectionCheckLoading.set(false);
    }
  }

  private applyCollectionFromApi(collection: StacCollectionResponse): void {
    const title = String(collection['title'] ?? '').trim();
    const description = String(collection['description'] ?? '').trim();
    const extent = collection['extent'] as Record<string, unknown> | undefined;
    const temporal = extent?.['temporal'] as Record<string, unknown> | undefined;
    const intervals = temporal?.['interval'] as unknown;
    let startDate = '';
    let endDate = '';
    if (Array.isArray(intervals) && intervals.length > 0) {
      const first = intervals[0];
      if (Array.isArray(first) && first.length >= 2) {
        startDate = this.normalizeLoadedDateTime(String(first[0] ?? ''));
        endDate = this.normalizeLoadedDateTime(String(first[1] ?? ''));
      }
    }

    this.collectionForm.patchValue({
      title,
      description,
      startDate,
      endDate,
    });

    const spatial = extent?.['spatial'] as Record<string, unknown> | undefined;
    const bboxes = spatial?.['bbox'] as unknown;
    if (Array.isArray(bboxes) && bboxes.length > 0) {
      const firstBbox = bboxes[0];
      if (Array.isArray(firstBbox) && firstBbox.length >= 4) {
        const tuple: BboxTuple = [
          Number(firstBbox[0]),
          Number(firstBbox[1]),
          Number(firstBbox[2]),
          Number(firstBbox[3]),
        ];
        if (tuple.every((value) => Number.isFinite(value))) {
          this.bbox.set(tuple);
          this.collectionMapGeometry.set({
            bbox: tuple,
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [tuple[0], tuple[1]],
                  [tuple[0], tuple[3]],
                  [tuple[2], tuple[3]],
                  [tuple[2], tuple[1]],
                  [tuple[0], tuple[1]],
                ],
              ],
            },
            geometryType: 'Polygon',
          });
        }
      }
    }
    this.formRevision.update((n) => n + 1);
  }

  private markResumedFromExistingApi(): void {
    this.resumedFromExistingApi.set(true);
    this.captureCollectionBaseline();
  }

  private async tryResumeExistingCollection(collectionId: string): Promise<void> {
    try {
      this.collectionCheckLoading.set(true);
      const collection = await this.ogcApi.getStacCollection(collectionId);
      if (!collection?.['id']) {
        this.existingCollectionDetected.emit(false);
        return;
      }

      const id = String(collection['id']).trim();
      this.applyCollectionFromApi(collection);
      this.createdCollectionId.set(id);
      this.markResumedFromExistingApi();
      this.existingCollectionDetected.emit(true);
      this.scheduleGeometryPreviewRefresh('collection');
      await this.loadExistingItems();
    } catch (error) {
      this.existingCollectionDetected.emit(false);
      if (!this.isCollectionCheckBenignError(error)) {
        this.notifyError(this.errorMessage(error));
      }
    } finally {
      this.collectionCheckLoading.set(false);
    }
  }

  setItemMode(mode: StacItemMode): void {
    if (mode === 'existing' && !this.showExistingItemTab() && this.flowScope() !== 'item-edit') {
      return;
    }
    this.existingItemDropdownOpen.set(false);
    if (mode === 'new') {
      this.selectedExistingItemId.set('');
      this.createdItemId.set(null);
      this.itemBaseline.set(null);
      this.clearExistingItemPreview();
    }
    this.itemMode.set(mode);
    if (mode === 'existing') {
      void this.loadExistingItems();
    }
  }

  setItemEditMode(mode: StacItemMode): void {
    if (this.flowScope() !== 'item-edit') {
      this.setItemMode(mode);
      return;
    }
    this.setItemMode(mode);
  }

  async onExistingItemSelected(itemId: string): Promise<void> {
    this.selectedExistingItemId.set(itemId);
    if (!itemId.trim()) {
      this.clearExistingItemPreview();
      return;
    }

    const collectionId = this.collectionId();
    if (!collectionId) {
      return;
    }

    try {
      this.existingItemDetailsLoading.set(true);
      const item = await this.ogcApi.getStacItem(collectionId, itemId.trim());
      this.applyExistingItemMetadata(item);
      if (this.flowScope() === 'item-edit' || this.isEditFlow()) {
        this.createdItemId.set(itemId.trim());
        this.captureItemBaseline();
        this.scheduleGeometryPreviewRefresh('item');
      }
    } catch (error) {
      this.clearExistingItemPreview();
      this.notifyError(this.errorMessage(error));
    } finally {
      this.existingItemDetailsLoading.set(false);
      this.formRevision.update((n) => n + 1);
    }
  }

  private clearExistingItemPreview(): void {
    this.itemForm.reset({ id: '', datetime: '' });
    this.itemGeometry.set(null);
    this.itemProperties.set([]);
    this.itemBaseline.set(null);
  }

  private applyExistingItemMetadata(item: StacItemPayload): void {
    const props = item.properties ?? {};
    const datetimeRaw = String(props['datetime'] ?? '').trim();
    const datetime = datetimeRaw ? this.normalizeLoadedDateTime(datetimeRaw) : '';

    this.itemForm.patchValue({
      id: item.id,
      datetime,
    });

    this.itemGeometry.set(this.geometrySelectionFromItem(item));

    const rows: ItemPropertyRow[] = [];
    for (const [key, value] of Object.entries(props)) {
      if (key.toLowerCase() === 'datetime') {
        continue;
      }
      rows.push(this.createPropertyRow(key, this.propertyValueToString(value)));
    }
    this.itemProperties.set(rows);
  }

  private geometrySelectionFromItem(item: StacItemPayload): StacGeometrySelection | null {
    const bbox = item.bbox;
    if (!bbox || bbox.length < 4) {
      return null;
    }
    const tuple: BboxTuple = [
      Number(bbox[0]),
      Number(bbox[1]),
      Number(bbox[2]),
      Number(bbox[3]),
    ];
    const geom = item.geometry;
    return {
      bbox: tuple,
      geometry: {
        type: geom?.type ?? 'Geometry',
        coordinates: geom?.coordinates ?? [],
      },
      geometryType: geom?.type ?? 'Geometry',
    };
  }

  private propertyValueToString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  sanitizeItemId(event: Event): void {
    const input = event.target as HTMLInputElement;
    const next = input.value.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 50);
    if (next !== input.value) {
      input.value = next;
      this.itemForm.controls.id.setValue(next);
    }
  }

  openItemGeometryMap(): void {
    this.closeCalendar();
    this.closeTimePopover();
    const current = this.itemGeometry();
    this.pendingItemGeometry.set(current);
    this.pendingBbox.set(current?.bbox ?? null);
    this.mapDrawMode.set('item-geometry');
    this.pendingGeometryType.set(current?.geometryType ?? '');
    this.showGeoJsonPaste.set(false);
    this.shapeMenuOpen.set(false);
    this.geoJsonHint.set(null);
    this.bboxMapOpen.set(true);
    window.setTimeout(() => this.initMap(), 0);
  }

  itemGeometryLabel(): string {
    const selection = this.itemGeometry();
    return selection
      ? `${selection.geometryType}: ${selection.bbox.map((value) => this.formatCoordinate(value)).join(', ')}`
      : '';
  }

  async loadExistingItems(): Promise<void> {
    const collectionId = this.collectionId();
    if (!collectionId) return;
    try {
      this.existingItemsLoading.set(true);
      const response = await this.ogcApi.listStacItems(collectionId);
      const items = response.features ?? response.items ?? [];
      this.existingItems.set(items);
      if (items.length === 0 && this.flowScope() !== 'item-edit') {
        this.itemMode.set('new');
      }
    } catch (error) {
      this.notifyError( this.errorMessage(error));
      this.existingItems.set([]);
      if (this.flowScope() !== 'item-edit') {
        this.itemMode.set('new');
      }
    } finally {
      this.existingItemsLoading.set(false);
    }
  }

  async submitItem(): Promise<void> {
    const collectionId = this.collectionId();
    if (!collectionId) {
      this.notifyError( 'Create the STAC collection before adding an item.');
      return;
    }

    if (this.itemMode() === 'existing' && this.showExistingItemTab() && this.flowScope() !== 'item-edit' && !this.isEditFlow()) {
      const existingId = this.selectedExistingItemId().trim();
      if (!existingId) {
        this.notifyError( 'Select an existing STAC item.');
        return;
      }
      this.createdItemId.set(existingId);
      if (this.flowScope() === 'embedded') {
        await this.syncDataUploadStatus(existingId);
        this.itemStepComplete.emit({ collectionId, itemId: existingId });
      } else {
        await this.prepareAssetsStep('existing');
        this.activeStep.set('assets');
      }
      return;
    }

    if (!this.itemForm.valid || !this.itemGeometry()) {
      this.itemForm.markAllAsTouched();
      if (!this.itemGeometry()) {
        this.notifyError('Upload or paste geometry for the STAC item.');
      }
      return;
    }

    const existingItemId = this.resolveExistingStacItemIdForSubmit();
    if (this.isEditFlow() && existingItemId && !this.hasItemChanges()) {
      if (this.flowScope() === 'embedded') {
        await this.syncDataUploadStatus(existingItemId);
        this.itemStepComplete.emit({ collectionId, itemId: existingItemId });
      } else {
        await this.prepareAssetsStep('existing');
        this.activeStep.set('assets');
      }
      return;
    }

    try {
      this.itemSubmitting.set(true);
      const payload = this.buildItemPayload(existingItemId);
      let itemId: string;
      if (existingItemId) {
        const updated = await this.ogcApi.patchStacItem(collectionId, existingItemId, payload);
        itemId = updated.id || existingItemId;
        this.captureItemBaseline();
      } else {
        const created = await this.ogcApi.createStacItem(collectionId, payload);
        itemId = created.id || payload.id;
      }
      this.createdItemId.set(itemId);
      this.notifySuccess('STAC item saved successfully.');
      if (this.flowScope() === 'item-edit') {
        if (!existingItemId) {
          await this.loadExistingItems();
          this.itemMode.set('existing');
          this.selectedExistingItemId.set(itemId);
        }
        return;
      }
      await this.syncDataUploadStatus(itemId);
      if (this.flowScope() === 'embedded') {
        this.itemStepComplete.emit({ collectionId, itemId });
      } else {
        await this.prepareAssetsStep(existingItemId ? 'existing' : 'new');
        this.activeStep.set('assets');
      }
    } catch (error) {
      const message = this.errorMessage(error);
      this.notifyError(message);
    } finally {
      this.itemSubmitting.set(false);
    }
  }

  addAssetRow(): void {
    if (!this.assetsForm.valid) {
      this.assetsForm.markAllAsTouched();
      this.notifyWarning(
        'Complete the previous asset by filling in all required fields before adding a new asset.',
      );
      return;
    }
    const newIndex = this.assetRows.length;
    this.assetRows.push(this.createAssetRow());
    this.expandedAssetIndex.set(newIndex);
    this.formRevision.update((n) => n + 1);
  }

  removeAssetRow(index: number): void {
    if (this.assetRows.length <= 1) return;
    this.assetRows.removeAt(index);
    this.uploadStates.update((states) => states.filter((_, i) => i !== index));
    const expanded = this.expandedAssetIndex();
    if (expanded !== null) {
      if (expanded >= this.assetRows.length) {
        this.expandedAssetIndex.set(Math.max(0, this.assetRows.length - 1));
      } else if (expanded > index) {
        this.expandedAssetIndex.set(expanded - 1);
      }
    }
    this.formRevision.update((n) => n + 1);
  }

  useAssetAccordion(): boolean {
    return this.assetRows.length > 1;
  }

  isAssetExpanded(index: number): boolean {
    if (!this.useAssetAccordion()) return true;
    return this.expandedAssetIndex() === index;
  }

  toggleAssetAccordion(index: number): void {
    if (!this.useAssetAccordion()) return;
    this.expandedAssetIndex.update((current) => (current === index ? null : index));
  }

  getAssetAccordionLabel(index: number): string {
    const title = String(this.assetRows.at(index).get('title')?.value ?? '').trim();
    return title || `Asset ${index + 1}`;
  }

  getAssetFileSize(index: number): number {
    const file = this.assetRows.at(index).get('fileObject')?.value as File | null;
    return file?.size ?? 0;
  }

  formatAssetFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024;
      unit += 1;
    }
    return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  }

  triggerAssetFileInput(index: number, event?: Event): void {
    if (event) {
      const source = event.currentTarget as HTMLElement;
      const body = source.closest<HTMLElement>('.stac-assets-accordion__body, .stac-assets-row');
      const input = body?.querySelector<HTMLInputElement>('.stac-assets-dropzone__input');
      if (input) {
        input.click();
        return;
      }
    }
    const inputs = this.hostEl.nativeElement.querySelectorAll<HTMLInputElement>(
      '.stac-assets-dropzone__input',
    );
    inputs.item(index)?.click();
  }

  onAssetDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    this.assetFileDragActive.set(index);
  }

  onAssetDragLeave(event: DragEvent, index: number): void {
    event.preventDefault();
    if (this.assetFileDragActive() === index) {
      this.assetFileDragActive.set(null);
    }
  }

  onAssetDrop(event: DragEvent, index: number): void {
    event.preventDefault();
    this.assetFileDragActive.set(null);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setAssetFile(index, file);
    }
  }

  clearAssetFile(index: number): void {
    const row = this.assetRows.at(index);
    row.patchValue({ fileName: '', fileObject: null });
    row.get('fileObject')?.markAsTouched();
    this.formRevision.update((n) => n + 1);
  }

  onAssetFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (file) {
      this.setAssetFile(index, file);
    }
    input.value = '';
  }

  private setAssetFile(index: number, file: File): void {
    const row = this.assetRows.at(index);
    row.patchValue({
      fileName: file.name,
      fileObject: file,
    });
    row.get('fileObject')?.markAsTouched();
    this.formRevision.update((n) => n + 1);
  }

  private async prepareAssetsStep(source?: AssetsItemSource): Promise<void> {
    if (source) {
      this.assetsItemSource.set(source);
    }
    this.existingItemAssets.set([]);
    this.closeAssetActionDropdown();
    await this.loadBuckets();
    if (this.assetsItemSource() === 'existing') {
      await this.loadExistingItemAssetsForStep();
    }
    this.assetsTab.set(this.showAssetsTabs() ? 'view' : 'upload');
  }

  private async loadExistingItemAssetsForStep(): Promise<void> {
    const collectionId = this.collectionId();
    const itemId = this.createdItemId()?.trim();
    if (!collectionId || !itemId) {
      return;
    }
    try {
      this.existingItemAssetsLoading.set(true);
      const item = await this.ogcApi.getStacItem(collectionId, itemId);
      this.existingItemAssets.set(
        parseStacItemAssets(item.assets as Record<string, unknown> | undefined),
      );
    } catch (error) {
      this.existingItemAssets.set([]);
      this.notifyError(this.errorMessage(error));
    } finally {
      this.existingItemAssetsLoading.set(false);
    }
  }

  setAssetsTab(tab: AssetsTab): void {
    this.closeAssetActionDropdown();
    this.assetsTab.set(tab);
  }

  formatExistingAssetSize(size: number | null): string {
    return formatAssetByteSize(size);
  }

  isAssetActionDropdownOpen(assetId: string): boolean {
    return this.assetActionDropdown()?.asset.id === assetId;
  }

  toggleAssetActionDropdown(event: MouseEvent, asset: StacExistingAssetRow): void {
    event.stopPropagation();
    if (this.isAssetActionDropdownOpen(asset.id)) {
      this.closeAssetActionDropdown();
      return;
    }
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = 52;
    const menuWidth = 160;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const openAbove = viewportHeight - rect.bottom < menuHeight && rect.top > menuHeight;
    const top = openAbove ? rect.top - menuHeight - 6 : rect.bottom + 6;
    const left = Math.min(Math.max(8, rect.right - menuWidth), viewportWidth - menuWidth - 8);
    this.assetActionDropdown.set({
      asset,
      top: Math.max(8, top),
      left,
      openAbove,
    });
  }

  closeAssetActionDropdown(): void {
    this.assetActionDropdown.set(null);
  }

  async onAssetActionDownload(): Promise<void> {
    const dropdown = this.assetActionDropdown();
    if (!dropdown) {
      return;
    }
    const asset = dropdown.asset;
    this.closeAssetActionDropdown();
    await this.downloadExistingAsset(asset);
  }

  async downloadExistingAsset(asset: StacExistingAssetRow): Promise<void> {
    if (this.downloadingAssetId()) {
      return;
    }
    try {
      this.downloadingAssetId.set(asset.id);
      const filename = sanitizeDownloadFilename(asset.title, asset.id);
      const blob = await this.ogcApi.downloadFromUrl(asset.href);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      this.notifyError(this.errorMessage(error));
    } finally {
      this.downloadingAssetId.set(null);
    }
  }

  async loadBuckets(): Promise<void> {
    try {
      this.bucketsLoading.set(true);
      const buckets = await this.ogcApi.listConfiguredS3Buckets();
      const privateBuckets = buckets.filter(
        (bucket) => String(bucket.readAccess ?? '').toUpperCase() === 'PRIVATE',
      );
      this.buckets.set(privateBuckets);
    } catch (error) {
      this.notifyError( this.errorMessage(error));
      this.buckets.set([]);
    } finally {
      this.bucketsLoading.set(false);
    }
  }

  async submitAssets(): Promise<void> {
    const collectionId = this.collectionId();
    const itemId = this.createdItemId();
    const bucketId = this.selectedBucketId().trim();

    if (!collectionId || !itemId) {
      this.notifyError( 'Create or select a STAC item before uploading assets.');
      return;
    }
    if (!bucketId) {
      this.notifyError( 'Select storage before uploading assets.');
      return;
    }
    if (!this.assetsForm.valid) {
      this.assetsForm.markAllAsTouched();
      return;
    }

    try {
      this.assetsSubmitting.set(true);
      this.uploadStates.set(
        this.assetRows.controls.map((row) => ({
          fileName: String(row.get('fileName')?.value ?? ''),
          progress: 0,
          status: 'Waiting',
        })),
      );

      const processes = (await this.ogcApi.getProcesses()).processes ?? [];
      const initiateProcessId = this.ogcApi.findProcessIdByTitle(
        processes,
        'S3InitiateMultipartUpload',
      );
      const completeProcessId = this.ogcApi.findProcessIdByTitle(
        processes,
        'S3CompleteMultipartUpload',
      );
      if (!initiateProcessId || !completeProcessId) {
        throw new Error('S3 multipart upload processes were not found.');
      }

      let anySuccess = false;
      for (let i = 0; i < this.assetRows.length; i++) {
        try {
          await this.uploadAssetRow(i, {
            collectionId,
            itemId,
            bucketId,
            initiateProcessId,
            completeProcessId,
          });
          anySuccess = true;
        } catch (error) {
          const status =
            error instanceof OgcAssetUploadConflictError
              ? 'File already exists'
              : 'Failed';
          this.patchUploadState(i, { status, progress: 0 });
          if (!(error instanceof OgcAssetUploadConflictError)) {
            this.notifyWarning(this.errorMessage(error));
          }
        }
      }

      if (!anySuccess) {
        throw new Error('No assets were uploaded successfully.');
      }

      await this.syncDataUploadStatus(itemId);
      this.notifySuccess('Assets uploaded successfully.');
      if (this.assetsItemSource() === 'existing') {
        await this.loadExistingItemAssetsForStep();
        if (this.showAssetsTabs()) {
          this.assetsTab.set('view');
        }
      }
    } catch (error) {
      const message = this.errorMessage(error);
      this.notifyError(message);
    } finally {
      this.assetsSubmitting.set(false);
    }
  }

  private async uploadAssetRow(
    index: number,
    params: {
      collectionId: string;
      itemId: string;
      bucketId: string;
      initiateProcessId: string;
      completeProcessId: string;
    },
  ): Promise<void> {
    const row = this.assetRows.at(index);
    const file = row.get('fileObject')?.value as File | null;
    if (!file) {
      throw new Error('Choose a file for every asset row.');
    }

    this.patchUploadState(index, { status: 'Initiating', progress: 2 });
    const initiated = await this.ogcApi.initiateStacAssetMultipartUpload({
      processId: params.initiateProcessId,
      collectionId: params.collectionId,
      itemId: params.itemId,
      bucketName: params.bucketId,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/octet-stream',
      s3BucketIdentifier: params.bucketId,
    });

    const uploadId = initiated.uploadId?.trim();
    const presignedUrls = initiated.presignedUrls ?? [];
    const chunkSize = initiated.chunkSize ?? 5 * 1024 * 1024;
    const s3KeyName = initiated.s3KeyName?.trim() || file.name;
    if (!uploadId || presignedUrls.length === 0) {
      throw new Error('Multipart upload could not be initiated.');
    }

    const sortedParts = [...presignedUrls].sort(
      (a, b) => a.partNumber - b.partNumber,
    );
    const completedParts: string[] = [];

    for (let partIndex = 0; partIndex < sortedParts.length; partIndex++) {
      const part = sortedParts[partIndex]!;
      const start = (part.partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const etag = await this.uploadPartWithRetry(part.url, chunk);
      completedParts.push(`${part.partNumber}:${etag}`);
      this.patchUploadState(index, {
        status: 'Uploading',
        progress: Math.max(
          5,
          Math.round(((partIndex + 1) / sortedParts.length) * 85),
        ),
      });
    }

    this.patchUploadState(index, { status: 'Completing', progress: 92 });
    const completed = await this.ogcApi.completeStacAssetMultipartUpload({
      processId: params.completeProcessId,
      uploadId,
      bucketName: params.bucketId,
      filePath: s3KeyName,
      parts: completedParts,
      s3BucketIdentifier: params.bucketId,
    });

    const status = String(completed.status ?? '').toLowerCase();
    if (status && status !== 'successful' && status !== 'success') {
      throw new Error(completed.message || 'Asset upload could not be completed.');
    }

    const assetKey = this.newAssetId();
    const asset: StacAssetPayload = {
      href: s3KeyName,
      title: String(row.get('title')?.value ?? '').trim(),
      description: String(row.get('description')?.value ?? '').trim(),
      type: file.type || 'application/octet-stream',
      roles: [String(row.get('role')?.value ?? 'data')],
      size: file.size,
      s3BucketId: params.bucketId,
    };

    this.patchUploadState(index, { status: 'Registering', progress: 96 });
    await this.ogcApi.patchStacItem(params.collectionId, params.itemId, {
      id: params.itemId,
      type: 'Feature',
      assets: {
        [assetKey]: asset,
      },
    });
    this.patchUploadState(index, { status: 'Completed', progress: 100 });
  }

  private buildItemPayload(existingItemId?: string | null): StacItemPayload {
    const geometry = this.itemGeometry();
    if (!geometry) {
      throw new Error('STAC item geometry is required.');
    }
    const raw = this.itemForm.getRawValue();
    const properties = this.buildPropertiesObject();
    properties['datetime'] = normalizeStacUtcDateTime(String(raw.datetime ?? '').trim());

    const id = (existingItemId?.trim() || String(raw.id ?? '').trim());
    if (!id) {
      throw new Error('STAC item id is required.');
    }

    return {
      id,
      type: 'Feature',
      bbox: geometry.bbox,
      geometry: geometry.geometry,
      properties,
    };
  }

  /**
   * Resolve the STAC item id for PATCH when editing an existing item.
   * Id is read-only in the UI; datetime, geometry, and properties are editable.
   */
  private resolveExistingStacItemIdForSubmit(): string | null {
    const created = this.createdItemId()?.trim();
    if (created) {
      return created;
    }
    if (this.flowScope() === 'item-edit' && this.itemMode() === 'existing') {
      const selected = this.selectedExistingItemId().trim();
      return selected || null;
    }
    return null;
  }

  private buildPropertiesObject(): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const row of this.itemProperties()) {
      const key = row.key.trim();
      if (!key) continue;
      result[key] = this.coercePropertyValue(row.value);
    }
    return result;
  }

  private coercePropertyValue(raw: string): unknown {
    const trimmed = raw.trim();
    if (trimmed === '') return '';
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (/^-?\d+$/.test(trimmed)) return Number(trimmed);
    if (/^-?\d+\.\d+$/.test(trimmed)) return Number(trimmed);
    return trimmed;
  }

  private createPropertyRow(key = '', value = ''): ItemPropertyRow {
    return { id: this.newAssetId(), key, value };
  }

  private async uploadPartWithRetry(
    url: string,
    chunk: Blob,
    attempt = 1,
  ): Promise<string> {
    try {
      const etag = await this.ogcApi.uploadToPresignedUrl(url, chunk);
      return etag.replace(/"/g, '');
    } catch (error) {
      if (attempt < 5 && this.isRetryableUploadError(error)) {
        await this.delay(Math.pow(2, attempt) * 1000);
        return this.uploadPartWithRetry(url, chunk, attempt + 1);
      }
      throw error;
    }
  }

  private isRetryableUploadError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const status = (error as { status?: number }).status;
    return status === 503;
  }

  private patchUploadState(index: number, patch: Partial<AssetUploadState>): void {
    this.uploadStates.update((states) =>
      states.map((state, i) => (i === index ? { ...state, ...patch } : state)),
    );
  }

  collectionId(): string {
    return (this.createdCollectionId() ?? this.itemId() ?? '').trim();
  }

  bucketIdentifier(bucket: OgcConfiguredS3Bucket): string {
    return String(bucket.s3BucketIdentifier ?? bucket.bucketName ?? '').trim();
  }

  toggleStorageDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.existingItemDropdownOpen.set(false);
    this.assetRoleDropdownIndex.set(null);
    this.storageDropdownOpen.update((open) => !open);
  }

  selectStorageBucket(bucketId: string): void {
    this.selectedBucketId.set(bucketId);
    this.storageDropdownOpen.set(false);
  }

  getSelectedBucketLabel(): string {
    const id = this.selectedBucketId();
    const bucket = this.buckets().find((entry) => this.bucketIdentifier(entry) === id);
    if (!bucket) {
      return id;
    }
    return `${this.bucketIdentifier(bucket)} (${bucket.readAccess})`;
  }

  toggleExistingItemDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.storageDropdownOpen.set(false);
    this.assetRoleDropdownIndex.set(null);
    this.existingItemDropdownOpen.update((open) => !open);
  }

  isAssetRoleDropdownOpen(index: number): boolean {
    return this.assetRoleDropdownIndex() === index;
  }

  toggleAssetRoleDropdown(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.storageDropdownOpen.set(false);
    this.existingItemDropdownOpen.set(false);
    this.assetRoleDropdownIndex.update((current) => (current === index ? null : index));
  }

  selectAssetRole(index: number, role: (typeof this.assetRoles)[number]): void {
    this.assetRows.at(index)?.get('role')?.setValue(role);
    this.assetRoleDropdownIndex.set(null);
  }

  selectExistingItemFromDropdown(itemId: string): void {
    this.existingItemDropdownOpen.set(false);
    void this.onExistingItemSelected(itemId);
  }

  private newAssetId(): string {
    return globalThis.crypto?.randomUUID?.() ?? this.fallbackUuid();
  }

  private fallbackUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const rand = Math.floor(Math.random() * 16);
      const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  private getCalendarFieldDate(field: CalendarField): Date | null {
    if (field === 'itemDate') {
      return parseStacDateTime(String(this.itemForm.controls.datetime.value ?? ''));
    }
    if (field === 'dateRange') {
      return this.getCalendarFieldDate('startDate');
    }
    return parseStacDateTime(String(this.collectionForm.controls[field].value ?? ''));
  }

  private extractDatePart(value: string): string | null {
    const parsed = parseStacDateTime(value);
    if (!parsed) return null;
    const year = parsed.getUTCFullYear();
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeLoadedDateTime(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) return '';
    try {
      return normalizeStacUtcDateTime(trimmed);
    } catch {
      return '';
    }
  }

  private currentCollectionBaseline(): CollectionBaseline {
    const raw = this.collectionForm.getRawValue();
    return {
      description: String(raw.description ?? ''),
      startDate: String(raw.startDate ?? ''),
      endDate: String(raw.endDate ?? ''),
      bbox: this.bbox(),
    };
  }

  private captureCollectionBaseline(): void {
    this.collectionBaseline.set(this.currentCollectionBaseline());
  }

  private currentItemBaseline(): ItemBaseline {
    const geometry = this.itemGeometry();
    return {
      datetime: String(this.itemForm.controls.datetime.value ?? ''),
      bbox: geometry?.bbox ?? null,
      geometryJson: geometry ? JSON.stringify(geometry.geometry) : '',
      propertiesJson: serializeItemProperties(this.buildPropertiesObject()),
    };
  }

  private captureItemBaseline(): void {
    this.itemBaseline.set(this.currentItemBaseline());
  }

  private parseIsoDate(value: string): Date | null {
    return parseStacDateTime(value);
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildCollectionPayload(): StacCollectionPayload {
    const raw = this.collectionForm.getRawValue();
    const bbox = this.bbox();
    if (!bbox) {
      throw new Error('Collection bounding box is required.');
    }

    return {
      type: 'Collection',
      id: this.itemId()!.trim(),
      title: String(raw.title ?? '').trim(),
      description: String(raw.description ?? '').trim(),
      license: 'proprietary',
      extent: {
        spatial: {
          bbox: [bbox],
          crs: CRS84,
        },
        temporal: {
          interval: [
            [
              normalizeStacUtcDateTime(String(raw.startDate ?? '')),
              normalizeStacUtcDateTime(String(raw.endDate ?? '')),
            ],
          ],
        },
      },
      datetime_key: 'datetime',
    };
  }

  private initMap(): void {
    const target = this.bboxMapEl()?.nativeElement;
    if (!target) return;

    this.destroyMap();

    this.vectorSource = new VectorSource();
    this.bboxOverlaySource = new VectorSource();
    const geometryLayer = new VectorLayer({
      source: this.vectorSource,
      zIndex: 6,
      style: (feature) => this.geometryFeatureStyle(feature),
    });
    const bboxLayer = new VectorLayer({
      source: this.bboxOverlaySource,
      zIndex: 5,
      style: new Style({
        stroke: new Stroke({
          color: '#2563eb',
          width: 2,
          lineDash: [8, 6],
        }),
        fill: new Fill({ color: 'rgba(59, 130, 246, 0.12)' }),
      }),
    });

    this.map = new OlMap({
      target,
      layers: [
        new TileLayer({
          source: new OSM({ crossOrigin: 'anonymous' }),
        }),
        bboxLayer,
        geometryLayer,
      ],
      view: new View({
        center: fromLonLat([78.9629, 20.5937]),
        zoom: 5,
      }),
      controls: [],
    });

    const selection =
      this.mapDrawMode() === 'item-geometry'
        ? this.pendingItemGeometry()
        : this.pendingCollectionGeometry();
    this.resetPendingMarkerCoords(selection);
    if (selection) {
      this.restoreGeometryOnMap(selection);
    }
    if (
      this.mapDrawMode() !== 'item-geometry' &&
      (!selection || this.activeDrawTool() === 'marker')
    ) {
      this.startDrawInteraction();
    }
    void this.loadMaharashtraMapOverlays(!selection);
    window.setTimeout(() => this.map?.updateSize(), 50);
  }

  private async loadMaharashtraMapOverlays(fitView: boolean): Promise<void> {
    if (!this.map) return;
    if (!this.boundaryGeoJsonUrl) {
      console.warn(
        '[Canvas] CANVAS_BOUNDARY_GEOJSON_URL is not provided. ' +
          'Boundary overlay will not render in the onboarding map preview. ' +
          'Add `{ provide: CANVAS_BOUNDARY_GEOJSON_URL, useValue: \'/assets/...\' }` to your app providers.',
      );
      return;
    }
    try {
      const layers = this.map.getLayers();
      const dimLayer = (this.maharashtraDimLayer ??=
        await createMaharashtraOutsideDimLayer(
          this.boundaryGeoJsonUrl,
          this.mapProjection,
        ));
      dimLayer.setZIndex(3);
      if (!layers.getArray().includes(dimLayer)) {
        layers.insertAt(1, dimLayer);
      }

      const boundaryLayer = (this.maharashtraBoundaryLayer ??=
        await createMaharashtraBoundaryLayer(
          this.boundaryGeoJsonUrl,
          this.mapProjection,
        ));
      boundaryLayer.setZIndex(4);
      if (!layers.getArray().includes(boundaryLayer)) {
        layers.insertAt(2, boundaryLayer);
      }

      if (fitView) {
        const extent = transformExtent(
          MAHARASHTRA_MAP_EXTENT,
          'EPSG:4326',
          this.mapProjection,
        );
        this.map.getView().fit(extent, {
          padding: [48, 48, 48, 48],
          maxZoom: 8,
          duration: 250,
        });
      }
    } catch (error) {
      console.error('Maharashtra boundary layer failed to load', error);
    }
  }

  private stopDrawInteraction(): void {
    if (!this.draw || !this.map) return;
    this.draw.abortDrawing();
    this.map.removeInteraction(this.draw);
    this.draw = null;
  }

  private startDrawInteraction(): void {
    if (!this.map || !this.vectorSource) return;
    if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = null;
    }

    const tool = this.activeDrawTool();
    let type: GeometryType = 'Polygon';
    let geometryFunction: ReturnType<typeof createBox> | undefined;
    if (tool === 'marker') {
      type = 'Point';
    } else if (tool === 'line') {
      type = 'LineString';
    } else if (tool === 'rectangle') {
      type = 'Circle';
      geometryFunction = createBox();
    } else if (tool === 'circle') {
      type = 'Circle';
    }

    const drawOptions: {
      source: VectorSource;
      type: GeometryType;
      stopClick: boolean;
      geometryFunction?: ReturnType<typeof createBox>;
    } = {
      source: this.vectorSource,
      type,
      stopClick: true,
    };
    if (geometryFunction) {
      drawOptions.geometryFunction = geometryFunction;
    }

    this.draw = new Draw(drawOptions);
    this.draw.on('drawstart', () => {
      if (this.activeDrawTool() !== 'marker') {
        this.clearMapDrawArtifacts();
        this.pendingBbox.set(null);
        this.pendingCollectionGeometry.set(null);
        this.pendingItemGeometry.set(null);
        this.pendingGeometryType.set('');
        this.geoJsonHint.set(null);
      }
    });

    this.draw.on('drawend', (event) => {
      const feature = event.feature as Feature<Geometry>;
      const geometry = this.normalizeFeatureGeometry(feature);
      if (!geometry) return;
      if (this.activeDrawTool() === 'marker') {
        feature.setGeometry(geometry);
        this.applyMarkerPlacement(feature);
        return;
      }
      this.applyGeometrySelection(geometry, feature, this.toolGeometryLabel(tool));
    });

    this.map.addInteraction(this.draw);
  }

  private normalizeFeatureGeometry(feature: Feature<Geometry>): Geometry | null {
    const geometry = feature.getGeometry();
    if (!geometry) return null;
    if (geometry instanceof CircleGeom) {
      const polygon = fromCircle(geometry, 64);
      feature.setGeometry(polygon);
      return polygon;
    }
    return geometry;
  }

  private applyGeometrySelection(
    geometry: Geometry,
    feature: Feature<Geometry>,
    label: string = geometry.getType() ?? 'Geometry',
  ): void {
    const bbox = this.geometryToBbox(geometry);
    if (!bbox) {
      this.geoJsonHint.set('Could not derive a bbox from that geometry.');
      return;
    }

    this.vectorSource?.clear();
    this.vectorSource?.addFeature(feature);
    this.updateBboxOverlay(this.bboxOverlaySource, bbox);
    this.pendingBbox.set(bbox);
    this.pendingGeometryType.set(label);
    this.geoJsonHint.set(`${label} selected. BBOX derived from its extent.`);

    const geoJsonGeometry = this.geometryToGeoJsonGeometry(feature);
    if (!geoJsonGeometry) {
      if (this.mapDrawMode() === 'item-geometry') {
        this.pendingItemGeometry.set(null);
      } else {
        this.pendingCollectionGeometry.set(null);
      }
      this.geoJsonHint.set('Could not convert that geometry to GeoJSON.');
      return;
    }

    const selection: StacGeometrySelection = {
      bbox,
      geometry: geoJsonGeometry,
      geometryType: label,
    };

    if (this.mapDrawMode() === 'item-geometry') {
      this.pendingItemGeometry.set(selection);
    } else {
      this.pendingCollectionGeometry.set(selection);
    }

    this.stopDrawInteraction();
  }

  private applyMarkerPlacement(feature: Feature<Geometry>): void {
    const coord = this.pointCoordFromFeature(feature);
    if (!coord) {
      return;
    }
    this.appendMarkerCoord(coord);
    this.syncMarkersSelection();
  }

  private syncMarkersSelection(): void {
    const coords = [...this.pendingMarkerCoords];
    if (coords.length === 0) {
      this.pendingBbox.set(null);
      this.pendingCollectionGeometry.set(null);
      this.pendingItemGeometry.set(null);
      this.pendingGeometryType.set('');
      this.bboxOverlaySource?.clear();
      return;
    }

    const bbox = this.bboxFromCoordinates(coords);
    const geometry = this.geometryFromMarkerCoordinates(coords);
    if (!bbox || !geometry) {
      return;
    }

    this.updateBboxOverlay(this.bboxOverlaySource, bbox);
    this.pendingBbox.set(bbox);
    const label = coords.length === 1 ? 'Point' : `${coords.length} Points`;
    this.pendingGeometryType.set(label);
    this.geoJsonHint.set(
      `${label} selected. BBOX derived from all marker locations.`,
    );

    const selection: StacGeometrySelection = {
      bbox,
      geometry,
      geometryType: label,
    };

    if (this.mapDrawMode() === 'item-geometry') {
      this.pendingItemGeometry.set(selection);
    } else {
      this.pendingCollectionGeometry.set(selection);
    }
  }

  private clearMapDrawArtifacts(): void {
    this.pendingMarkerCoords = [];
    this.vectorSource?.clear();
    this.bboxOverlaySource?.clear();
  }

  private appendMarkerCoord(coord: [number, number]): void {
    const key = `${coord[0]},${coord[1]}`;
    const exists = this.pendingMarkerCoords.some(
      ([lon, lat]) => `${lon},${lat}` === key,
    );
    if (!exists) {
      this.pendingMarkerCoords.push(coord);
    }
  }

  private markerCoordsFromGeometry(
    geometry: GeoJsonGeometry,
  ): [number, number][] {
    if (geometry.type === 'Point') {
      return [geometry.coordinates as [number, number]];
    }
    if (geometry.type === 'MultiPoint') {
      return (geometry.coordinates as [number, number][]).map(
        ([lon, lat]) =>
          [this.clampLongitude(lon), this.clampLatitude(lat)] as [
            number,
            number,
          ],
      );
    }
    return [];
  }

  private pointCoordFromFeature(
    feature: Feature<Geometry>,
  ): [number, number] | null {
    const geometry = feature.getGeometry();
    if (!(geometry instanceof Point)) {
      return null;
    }
    const [lon, lat] = transform(
      geometry.getCoordinates(),
      'EPSG:3857',
      'EPSG:4326',
    );
    return [this.clampLongitude(lon), this.clampLatitude(lat)];
  }

  private resetPendingMarkerCoords(
    selection: StacGeometrySelection | null,
  ): void {
    if (selection && this.isMarkerGeometry(selection.geometry)) {
      this.pendingMarkerCoords = this.markerCoordsFromGeometry(
        selection.geometry,
      );
      return;
    }
    this.pendingMarkerCoords = [];
  }

  private bboxFromCoordinates(coords: [number, number][]): BboxTuple | null {
    if (coords.length === 0) {
      return null;
    }

    let west = coords[0]![0];
    let south = coords[0]![1];
    let east = coords[0]![0];
    let north = coords[0]![1];

    for (const [lon, lat] of coords) {
      west = Math.min(west, lon);
      south = Math.min(south, lat);
      east = Math.max(east, lon);
      north = Math.max(north, lat);
    }

    return [west, south, east, north];
  }

  private geometryFromMarkerCoordinates(
    coords: [number, number][],
  ): GeoJsonGeometry | null {
    if (coords.length === 0) {
      return null;
    }
    if (coords.length === 1) {
      return { type: 'Point', coordinates: coords[0]! };
    }
    return { type: 'MultiPoint', coordinates: coords };
  }

  private isMarkerGeometry(geometry: GeoJsonGeometry): boolean {
    return geometry.type === 'Point' || geometry.type === 'MultiPoint';
  }

  private addMarkerGeometriesToSource(
    source: VectorSource,
    geometry: GeoJsonGeometry,
  ): void {
    const format = new GeoJSON();
    if (geometry.type === 'Point') {
      const feature = format.readFeature(
        { type: 'Feature', properties: {}, geometry },
        { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
      ) as Feature<Geometry>;
      source.addFeature(feature);
      return;
    }

    if (geometry.type === 'MultiPoint') {
      const coordinates = geometry.coordinates as [number, number][];
      for (const coordinate of coordinates) {
        const feature = format.readFeature(
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: coordinate },
          },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
        ) as Feature<Geometry>;
        source.addFeature(feature);
      }
    }
  }

  private extentFromBbox(bbox: BboxTuple): number[] | null {
    const [west, south, east, north] = bbox;
    const min = transform([west, south], 'EPSG:4326', 'EPSG:3857');
    const max = transform([east, north], 'EPSG:4326', 'EPSG:3857');
    return [min[0], min[1], max[0], max[1]];
  }

  private restoreGeometryOnMap(selection: StacGeometrySelection | null): void {
    if (!selection || !this.map || !this.vectorSource) return;

    this.vectorSource.clear();
    if (this.isMarkerGeometry(selection.geometry)) {
      this.pendingMarkerCoords = this.markerCoordsFromGeometry(
        selection.geometry,
      );
      this.addMarkerGeometriesToSource(this.vectorSource, selection.geometry);
      const bbox =
        this.bboxFromCoordinates(this.pendingMarkerCoords) ?? selection.bbox;
      this.updateBboxOverlay(this.bboxOverlaySource, bbox);
      const extent = this.extentFromBbox(bbox);
      if (extent?.every((value) => Number.isFinite(value))) {
        this.map.getView().fit(extent, {
          padding: [48, 48, 48, 48],
          maxZoom: 14,
          duration: 250,
        });
      }
      return;
    }

    const format = new GeoJSON();
    const feature = format.readFeature(
      { type: 'Feature', properties: {}, geometry: selection.geometry },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
    ) as Feature<Geometry>;

    const geometry = this.normalizeFeatureGeometry(feature);
    if (!geometry) return;
    this.vectorSource.addFeature(feature);

    this.updateBboxOverlay(this.bboxOverlaySource, selection.bbox);

    const extent = this.extentFromBbox(selection.bbox);
    if (extent?.every((value) => Number.isFinite(value))) {
      this.map.getView().fit(extent, {
        padding: [48, 48, 48, 48],
        maxZoom: 14,
        duration: 250,
      });
    }
  }

  private bboxToRectangleGeometry(bbox: BboxTuple): GeoJsonGeometry {
    const [west, south, east, north] = bbox;
    return {
      type: 'Polygon',
      coordinates: [
        [
          [west, south],
          [east, south],
          [east, north],
          [west, north],
          [west, south],
        ],
      ],
    };
  }

  private geometryToBbox(geometry: Geometry): BboxTuple | null {
    const extent = geometry.getExtent();
    if (!extent || !extent.every((value) => Number.isFinite(value))) {
      return null;
    }
    const [minLon, minLat, maxLon, maxLat] = transformExtent(
      extent,
      'EPSG:3857',
      'EPSG:4326',
    );
    return [
      this.clampLongitude(minLon),
      this.clampLatitude(minLat),
      this.clampLongitude(maxLon),
      this.clampLatitude(maxLat),
    ];
  }

  private geometryToGeoJsonGeometry(feature: Feature<Geometry>): GeoJsonGeometry | null {
    const format = new GeoJSON();
    const geoJson = format.writeFeatureObject(feature, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    }) as { geometry?: Partial<GeoJsonGeometry> };
    const geometry = geoJson.geometry;
    if (
      geometry &&
      typeof geometry.type === 'string' &&
      'coordinates' in geometry
    ) {
      return {
        type: geometry.type,
        coordinates: geometry.coordinates,
      };
    }
    return null;
  }

  private toolGeometryLabel(tool: MapDrawTool): string {
    switch (tool) {
      case 'marker':
        return 'Point';
      case 'line':
        return 'LineString';
      case 'rectangle':
        return 'Rectangle';
      case 'circle':
        return 'Circle';
      default:
        return 'Polygon';
    }
  }

  private applyUploadedGeoJson(text: string): void {
    if (!this.map) {
      throw new Error('Map is not ready yet.');
    }
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const source =
      parsed?.['type'] &&
      parsed['type'] !== 'Feature' &&
      parsed['type'] !== 'FeatureCollection'
        ? { type: 'Feature', properties: {}, geometry: parsed }
        : parsed;
    const format = new GeoJSON();
    const features = format.readFeatures(source, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    }) as Feature<Geometry>[];

    if (features.length !== 1) {
      throw new Error('Upload GeoJSON with exactly one feature or geometry.');
    }

    const feature = features[0]!;
    const geometry = this.normalizeFeatureGeometry(feature);
    if (!geometry) {
      throw new Error('The uploaded GeoJSON does not contain a geometry.');
    }

    this.applyGeometrySelection(geometry, feature, geometry.getType());
    const extent = geometry.getExtent();
    if (extent?.every((value) => Number.isFinite(value))) {
      this.map.getView().fit(extent, {
        padding: [48, 48, 48, 48],
        maxZoom: 14,
        duration: 250,
      });
    }
  }

  private destroyMap(): void {
    this.stopDrawInteraction();
    this.pendingMarkerCoords = [];
    this.vectorSource?.clear();
    this.bboxOverlaySource?.clear();
    this.vectorSource = null;
    this.bboxOverlaySource = null;
    this.maharashtraBoundaryLayer = null;
    this.maharashtraDimLayer = null;
    this.map?.setTarget(undefined);
    this.map = null;
  }

  private scheduleGeometryPreviewRefresh(context: 'collection' | 'item'): void {
    const refresh = () => this.refreshGeometryPreviewMap(context);
    window.setTimeout(refresh, 0);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        refresh();
        window.setTimeout(() => this.previewMap?.updateSize(), 100);
        window.setTimeout(() => this.previewMap?.updateSize(), 350);
      });
    });
  }

  private refreshGeometryPreviewMap(context: 'collection' | 'item'): void {
    const target = this.bboxPreviewMapEl()?.nativeElement;
    const geometrySelection =
      context === 'collection' ? this.collectionMapGeometry() : this.itemGeometry();
    const box =
      context === 'collection' ? this.bbox() : (this.itemGeometry()?.bbox ?? null);
    if (!target || !box) {
      this.destroyBboxPreviewMap();
      return;
    }

    if (!this.previewMap) {
      this.previewGeometrySource = new VectorSource();
      this.previewBboxOverlaySource = new VectorSource();
      const geometryLayer = new VectorLayer({
        source: this.previewGeometrySource,
        zIndex: 4,
        style: (feature) => this.geometryFeatureStyle(feature),
      });
      const bboxLayer = new VectorLayer({
        source: this.previewBboxOverlaySource,
        zIndex: 3,
        style: new Style({
          stroke: new Stroke({
            color: '#2563eb',
            width: 2,
            lineDash: [8, 6],
          }),
          fill: new Fill({ color: 'rgba(59, 130, 246, 0.12)' }),
        }),
      });

      this.previewMap = new OlMap({
        target,
        layers: [
          new TileLayer({ source: new OSM({ crossOrigin: 'anonymous' }) }),
          bboxLayer,
          geometryLayer,
        ],
        view: new View({
          center: fromLonLat([78.9629, 20.5937]),
          zoom: 5,
        }),
        controls: [],
        interactions: [],
      });
      void this.loadMaharashtraPreviewMapOverlays(false);
    } else {
      const currentTarget = this.previewMap.getTargetElement();
      if (currentTarget !== target) {
        this.previewMap.setTarget(target);
      }
    }

    const format = new GeoJSON();
    this.previewGeometrySource?.clear();
    if (geometrySelection?.geometry) {
      if (this.isMarkerGeometry(geometrySelection.geometry)) {
        this.addMarkerGeometriesToSource(
          this.previewGeometrySource!,
          geometrySelection.geometry,
        );
      } else {
        const geometryFeature = format.readFeature(
          {
            type: 'Feature',
            properties: {},
            geometry: geometrySelection.geometry,
          },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
        ) as Feature<Geometry>;
        this.previewGeometrySource?.addFeature(geometryFeature);
      }
    }

    this.updateBboxOverlay(this.previewBboxOverlaySource, box);

    const extent = this.extentFromBbox(box);
    if (extent?.every((value) => Number.isFinite(value))) {
      this.previewMap.getView().fit(extent, {
        padding: [24, 24, 24, 24],
        maxZoom: 12,
        duration: 200,
      });
    }

    window.setTimeout(() => this.previewMap?.updateSize(), 50);
    window.setTimeout(() => this.previewMap?.updateSize(), 200);
  }

  private async loadMaharashtraPreviewMapOverlays(fitView: boolean): Promise<void> {
    if (!this.previewMap) return;
    if (!this.boundaryGeoJsonUrl) return;
    try {
      const layers = this.previewMap.getLayers();
      const dimLayer = (this.previewMaharashtraDimLayer ??=
        await createMaharashtraOutsideDimLayer(
          this.boundaryGeoJsonUrl,
          this.mapProjection,
        ));
      dimLayer.setZIndex(1);
      if (!layers.getArray().includes(dimLayer)) {
        layers.insertAt(1, dimLayer);
      }

      const boundaryLayer = (this.previewMaharashtraBoundaryLayer ??=
        await createMaharashtraBoundaryLayer(
          this.boundaryGeoJsonUrl,
          this.mapProjection,
        ));
      boundaryLayer.setZIndex(2);
      if (!layers.getArray().includes(boundaryLayer)) {
        layers.insertAt(2, boundaryLayer);
      }

      if (fitView) {
        const extent = transformExtent(
          MAHARASHTRA_MAP_EXTENT,
          'EPSG:4326',
          this.mapProjection,
        );
        this.previewMap.getView().fit(extent, {
          padding: [48, 48, 48, 48],
          maxZoom: 8,
          duration: 250,
        });
      }
    } catch (error) {
      console.error('Maharashtra preview boundary layer failed to load', error);
    }
  }

  private destroyBboxPreviewMap(): void {
    this.previewGeometrySource?.clear();
    this.previewBboxOverlaySource?.clear();
    this.previewGeometrySource = null;
    this.previewBboxOverlaySource = null;
    this.previewMap?.setTarget(undefined);
    this.previewMap = null;
    this.previewMaharashtraBoundaryLayer = null;
    this.previewMaharashtraDimLayer = null;
  }

  private geometryFeatureStyle(feature: FeatureLike): Style {
    const geom = feature.getGeometry();
    if (geom instanceof Point || geom instanceof MultiPoint) {
      return this.getPlacementMarkerStyle();
    }
    if (geom instanceof LineString || geom instanceof MultiLineString) {
      return new Style({
        stroke: new Stroke({ color: '#00a66f', width: 3 }),
      });
    }
    return new Style({
      stroke: new Stroke({ color: '#00a66f', width: 2 }),
      fill: new Fill({ color: 'rgba(0, 208, 132, 0.16)' }),
    });
  }

  private getPlacementMarkerStyle(): Style {
    if (!this.placementMarkerStyle) {
      const iconUrl = `${this.iconsBaseUrl.replace(/\/$/, '')}/add-location-map.svg`;
      this.placementMarkerStyle = new Style({
        image: new Icon({
          src: iconUrl,
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          width: 32,
        }),
      });
    }
    return this.placementMarkerStyle;
  }

  private updateBboxOverlay(
    source: VectorSource | null,
    bbox: BboxTuple | null,
  ): void {
    source?.clear();
    if (!bbox) {
      return;
    }
    const format = new GeoJSON();
    const feature = format.readFeature(
      {
        type: 'Feature',
        properties: {},
        geometry: this.bboxToRectangleGeometry(bbox),
      },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
    ) as Feature<Geometry>;
    source?.addFeature(feature);
  }

  private zoomMapBy(map: OlMap | null, delta: number): void {
    if (!map) {
      return;
    }
    const view = map.getView();
    view.animate({
      zoom: (view.getZoom() ?? 5) + delta,
      duration: 200,
    });
  }

  private formatGeoJsonText(text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return '';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return text;
    }
  }

  private clampLongitude(value: number): number {
    return Math.max(-180, Math.min(180, Number(value.toFixed(8))));
  }

  private clampLatitude(value: number): number {
    return Math.max(-90, Math.min(90, Number(value.toFixed(8))));
  }

  private formatCoordinate(value: number): string {
    return Number.isFinite(value) ? value.toFixed(6) : '';
  }

  private async syncDataUploadStatus(stacItemId?: string | null): Promise<void> {
    const catalogueItemId = this.collectionId();
    if (!catalogueItemId) return;
    await syncRasterDataUploadStatus(
      this.catalogueApi,
      this.ogcApi,
      catalogueItemId,
      stacItemId ?? this.createdItemId(),
    );
  }

  private notifyError(message: string): void {
    this.onboardingError.emit(message);
  }

  private notifyWarning(message: string): void {
    this.onboardingWarning.emit(message);
  }

  private notifySuccess(message: string): void {
    this.onboardingSuccess.emit(message);
  }

  private isCollectionCheckBenignError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = Number((error as { status?: number }).status);
      return status === 404 || status === 405;
    }
    return false;
  }

  private errorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const err = error as {
        error?: { description?: string; detail?: string; message?: string };
        message?: string;
        statusText?: string;
      };
      const fromBody =
        err.error?.description ??
        err.error?.detail ??
        err.error?.message ??
        err.message ??
        err.statusText;
      if (typeof fromBody === 'string' && fromBody.trim()) {
        return fromBody.trim();
      }
    }
    return 'Failed to create STAC collection. Please try again.';
  }
}
